import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Camera, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { compressImage, formatFileSize } from "@/lib/imageCompression";

export interface PhotoFile {
  file: File;
  preview: string;
  attachmentType: "meter" | "chlorine_tank" | "other";
  originalSize?: number;
  compressedSize?: number;
}

interface PhotoUploadProps {
  onPhotosChange: (photos: PhotoFile[]) => void;
  photos: PhotoFile[];
  isOffline?: boolean;
}

export default function PhotoUpload({ onPhotosChange, photos, isOffline = false }: PhotoUploadProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<"meter" | "chlorine_tank" | "other">("meter");

  const handleFileSelect = async (files: FileList | null, type: "meter" | "chlorine_tank" | "other") => {
    if (!files || files.length === 0) return;

    const newPhotos: PhotoFile[] = [];
    
    toast.info(`Compressing ${files.length} ${files.length === 1 ? 'image' : 'images'}...`);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      
      // Validate file size (max 16MB before compression)
      if (file.size > 16 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 16MB limit`);
        continue;
      }
      
      try {
        // Compress image
        const compressionResult = await compressImage(file);
        const compressedFile = compressionResult.file;
        
        // Create preview URL from compressed file
        const preview = URL.createObjectURL(compressedFile);
        
        newPhotos.push({
          file: compressedFile,
          preview,
          attachmentType: type,
          originalSize: compressionResult.originalSize,
          compressedSize: compressionResult.compressedSize,
        });
      } catch (error) {
        console.error(`Error compressing ${file.name}:`, error);
        toast.error(`Failed to compress ${file.name}`);
      }
    }
    
    if (newPhotos.length > 0) {
      const totalOriginal = newPhotos.reduce((sum, p) => sum + (p.originalSize || 0), 0);
      const totalCompressed = newPhotos.reduce((sum, p) => sum + (p.compressedSize || 0), 0);
      const ratio = totalOriginal > 0 ? ((totalOriginal - totalCompressed) / totalOriginal) * 100 : 0;
      
      if (ratio > 5) {
        toast.success(
          `${newPhotos.length} ${newPhotos.length === 1 ? 'photo' : 'photos'} compressed from ${formatFileSize(totalOriginal)} to ${formatFileSize(totalCompressed)} (${ratio.toFixed(0)}% smaller)`
        );
      } else {
        toast.success(
          `${newPhotos.length} ${newPhotos.length === 1 ? 'photo' : 'photos'} ready. Total size: ${formatFileSize(totalCompressed)}`
        );
      }
    }
    
    onPhotosChange([...photos, ...newPhotos]);
  };

  const handleRemovePhoto = (index: number) => {
    // Revoke preview URL to free memory
    URL.revokeObjectURL(photos[index]!.preview);
    onPhotosChange(photos.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, type: "meter" | "chlorine_tank" | "other") => {
    const updated = [...photos];
    updated[index] = { ...updated[index]!, attachmentType: type };
    onPhotosChange(updated);
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={isOffline}
          onClick={() => cameraInputRef.current?.click()}
          className="flex-1"
        >
          <Camera className="w-4 h-4 mr-2" />
          Take Photo
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={isOffline}
          onClick={() => fileInputRef.current?.click()}
          className="flex-1"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Photos
        </Button>
      </div>

      {isOffline && (
        <p className="text-xs text-muted-foreground bg-orange-50 dark:bg-orange-950/20 p-2 rounded border-orange-200 dark:border-orange-800">
          Photos upload when you're back online.
        </p>
      )}

      {/* Hidden file inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files, selectedType)}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFileSelect(e.target.files, selectedType)}
      />

      {/* Type selector for next upload */}
      <div>
        <Label htmlFor="attachment-type">Photo Type (for next upload)</Label>
        <Select value={selectedType} onValueChange={(v) => setSelectedType(v as any)}>
          <SelectTrigger id="attachment-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="meter">Meter Reading</SelectItem>
            <SelectItem value="chlorine_tank">Chlorine Tank</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Photo previews */}
      {photos.length > 0 && (
        <div className="space-y-3">
          <Label>Attached Photos ({photos.length})</Label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo.preview}
                  alt={`Preview ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border"
                />
                <Button
                  type="button"
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRemovePhoto(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
                <div className="mt-1">
                  <Select
                    value={photo.attachmentType}
                    onValueChange={(v) => handleTypeChange(index, v as any)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="meter">Meter</SelectItem>
                      <SelectItem value="chlorine_tank">Chlorine</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
